import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { ApiRequestError } from "../../api/client.ts";
import { fetchCameras, type Camera } from "../../api/cameras.ts";
import {
  getAppRole,
  setPreviewCameraId as persistPreviewCameraId,
} from "../../lib/dashboardSession.ts";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { StandardTablePage } from "./table-page/StandardTablePage.tsx";
import { CameraGridSnapshot } from "./CameraGridSnapshot.tsx";
import { LivePreviewPane } from "./LivePreviewPane.tsx";
import { CamerasEmptyPanel } from "./CamerasEmptyPanel.tsx";

export type DashboardCameraSectionProps = {
  previewCameraId: string | null;
  onPreviewCameraIdChange: (id: string | null) => void;
  liveToolbarTrailing?: ReactNode;
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatOnvifDisplay(
  cam: Pick<Camera, "onvifEnabled" | "onvifHost" | "onvifPort">,
): string {
  if (cam.onvifEnabled && cam.onvifHost)
    return `${cam.onvifHost}:${cam.onvifPort ?? ""}`;
  return "—";
}

function formatOnvifTooltip(
  cam: Pick<Camera, "onvifEnabled" | "onvifHost" | "onvifPort">,
): string | undefined {
  if (cam.onvifEnabled && cam.onvifHost)
    return `${cam.onvifHost}:${cam.onvifPort ?? ""}`;
  return undefined;
}

function sortCameras(
  rows: Camera[],
  col: string | null,
  dir: "asc" | "desc",
): Camera[] {
  const out = [...rows];
  const mul = dir === "asc" ? 1 : -1;
  if (col === "name") {
    out.sort((a, b) => mul * a.name.localeCompare(b.name));
  } else if (col === "createdAt") {
    out.sort(
      (a, b) => mul * (Date.parse(a.createdAt) - Date.parse(b.createdAt)),
    );
  }
  return out;
}

export function DashboardCameraSection({
  previewCameraId,
  onPreviewCameraIdChange,
  liveToolbarTrailing,
}: DashboardCameraSectionProps) {
  const theme = useTheme();
  const liveDialogFullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [role] = useState(() => getAppRole());
  const isAdmin = role === "admin";
  const [liveViewOpen, setLiveViewOpen] = useState(false);

  const [cameras, setCameras] = useState<Camera[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const [filterId, setFilterId] = useState<string>("");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [sortColumnId] = useState<string | null>("name");
  const [sortDir] = useState<"asc" | "desc">("asc");

  const loadCameras = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await fetchCameras(searchQuery);
      setCameras(data);
    } catch (e) {
      const msg =
        e instanceof ApiRequestError
          ? e.message
          : e instanceof Error
            ? e.message
            : "Unable to load cameras.";
      setLoadError(msg);
      setCameras([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    const t = window.setTimeout(() => setSearchQuery(searchInput), 250);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    void loadCameras();
  }, [loadCameras]);

  const filtered = useMemo(() => {
    if (!filterId) return cameras;
    return cameras.filter((c) => c.id === filterId);
  }, [cameras, filterId]);

  const sorted = useMemo(
    () => sortCameras(filtered, sortColumnId, sortDir),
    [filtered, sortColumnId, sortDir],
  );

  const totalItems = sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(page, totalPages);
  const showCamerasEmptyOrError =
    Boolean(loadError) || (!loading && cameras.length === 0);
  const pageRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, safePage, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, filterId]);

  const handleOpenPreview = useCallback(
    (id: string) => {
      onPreviewCameraIdChange(id);
      persistPreviewCameraId(id);
      setLiveViewOpen(true);
    },
    [onPreviewCameraIdChange],
  );

  const handlePreviewSelect = useCallback(
    (id: string) => {
      onPreviewCameraIdChange(id);
      persistPreviewCameraId(id);
    },
    [onPreviewCameraIdChange],
  );

  const filterSelect = (
    <select
      className="ds-cam-filter-select"
      aria-label="Filter by camera"
      value={filterId}
      onChange={(e) => setFilterId(e.target.value)}
    >
      <option value="">All cameras</option>
      {cameras.map((c) => (
        <option key={c.id} value={c.id}>
          {c.name}
        </option>
      ))}
    </select>
  );

  return (
    <>
      <StandardTablePage
        title="Cameras"
        searchPlaceholder="Search Camera..."
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        toolbarAfterView={filterSelect}
        showViewToggle={false}
        totalItems={showCamerasEmptyOrError ? 0 : totalItems}
        page={showCamerasEmptyOrError ? 1 : safePage}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
      >
        {loading ? (
          <p
            className="ds-text-secondary"
            style={{ margin: "var(--space-4, 16px)" }}
          >
            Loading cameras…
          </p>
        ) : showCamerasEmptyOrError ? (
          <CamerasEmptyPanel
            variant="cameras"
            reason={loadError ? "error" : "empty"}
            isAdmin={isAdmin}
            onRetry={loadError ? () => void loadCameras() : undefined}
            errorDetail={loadError}
          />
        ) : (
          <div className="ds-cam-grid">
            {pageRows.map((cam) => (
              <div
                key={cam.id}
                role="button"
                tabIndex={0}
                className="ds-cam-card"
                onClick={() => handleOpenPreview(cam.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleOpenPreview(cam.id);
                  }
                }}
              >
                <div className="ds-cam-card__shot">
                  <CameraGridSnapshot
                    cameraId={cam.id}
                    cam360Rtsp={cam.cam360Rtsp}
                    version={cam.updatedAt}
                  />
                </div>
                <div className="ds-cam-card__body">
                  <p className="ds-cam-card__title" title={cam.name}>
                    {cam.name}
                  </p>
                  <p
                    className="ds-cam-card__meta"
                    title={cam.cam360Rtsp ?? undefined}
                  >
                    360: {cam.cam360Rtsp ?? "—"}
                  </p>
                  <p
                    className="ds-cam-card__meta"
                    title={cam.ptzRtsp ?? undefined}
                  >
                    PTZ: {cam.ptzRtsp ?? "—"}
                  </p>
                  <p
                    className="ds-cam-card__meta"
                    title={formatOnvifTooltip(cam)}
                  >
                    ONVIF: {formatOnvifDisplay(cam)}
                  </p>
                  <p className="ds-cam-card__meta" title={cam.createdAt}>
                    Created: {formatDate(cam.createdAt)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </StandardTablePage>

      <Dialog
        open={liveViewOpen}
        onClose={() => setLiveViewOpen(false)}
        fullScreen={liveDialogFullScreen}
        maxWidth="xl"
        fullWidth
        scroll="paper"
        aria-labelledby="ds-live-view-dialog-title"
        PaperProps={{
          sx: {
            display: "flex",
            flexDirection: "column",
            maxHeight: liveDialogFullScreen ? "100%" : "calc(100% - 48px)",
            height: liveDialogFullScreen ? "100%" : "min(92vh, 960px)",
            m: liveDialogFullScreen ? 0 : 2,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          component="div"
          id="ds-live-view-dialog-title"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            pr: 1,
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: 18, fontWeight: 600 }}>Live View</span>
          <IconButton
            type="button"
            edge="end"
            color="inherit"
            aria-label="Close live view"
            onClick={() => setLiveViewOpen(false)}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            flex: "1 1 auto",
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            px: 2,
            py: 1,
            pt: 1,
          }}
        >
          <Box className="ds-live-preview-modal-body">
            <LivePreviewPane
              cameras={cameras}
              previewCameraId={previewCameraId}
              onSelectCamera={handlePreviewSelect}
              loadError={loadError}
              onRetry={() => void loadCameras()}
              toolbarTrailing={liveToolbarTrailing}
            />
          </Box>
        </DialogContent>
      </Dialog>

    </>
  );
}
